import React, { useEffect, useRef, useState } from 'react';
import type { ShipBatchOrderResult } from '../../schemas/apiResponse.schemas';
import { fetchShipLabelStatus, fetchSingleItemPackageWeights, reprintShipLabels, shipBatchOrders } from '../../services/shipStationAPI';
import { getApiErrorMessage } from '../../services/errorUtils';
import { ScannerSection, closeCameraAfterScanFlash } from '../scanner';
import {
  BOX_SCAN_NO_TEXT_MESSAGE,
  BOX_SCAN_STATUSES,
  boxScanNoDimsMessage,
  readBoxDimensionsFromImage,
} from './boxDimensionScan';
import { USPS_GROUND_ADVANTAGE } from '@/shared/types/shipBatch.types';
import {
  getRememberedShipLabelPdfs,
  hasRememberedShipLabelPdf,
  rememberShipLabelPdf,
} from '../../utils/shipLabelPdfMemory';
import { splitOunces } from '@/shared/utils/weightOz';
import LbsOzWeightInputs from '../shared/LbsOzWeightInputs';
import '../labels/PrintBoxLabel.css';
import './SingleSkuOrdersList.css';

export interface ShipOrdersModalOrder {
  readonly orderId: string;
  readonly orderNumber: string;
}

export interface ShipOrdersModalProps {
  readonly subtitle: string;
  readonly orders: ReadonlyArray<ShipOrdersModalOrder>;
  readonly sku?: string;
  readonly showUspsShipButton?: boolean;
  readonly showReprintButton?: boolean;
  readonly onClose: () => void;
  readonly onFullyShipped?: () => void;
}

type ShipConfirmAction = 'default' | 'usps';

function dimString(value: number): string {
  return String(value);
}

function clearZeroOnFocus(value: string, setValue: (next: string) => void): void {
  if (value.trim() === '') {
    return;
  }
  if (Number(value) === 0) {
    setValue('');
  }
}

function ShipOrdersModal({
  subtitle,
  orders,
  sku,
  showUspsShipButton = false,
  showReprintButton = false,
  onClose,
  onFullyShipped,
}: ShipOrdersModalProps): React.ReactElement {
  const [lbs, setLbs] = useState('0');
  const [oz, setOz] = useState(sku ? '0' : '8');
  const [lengthIn, setLengthIn] = useState(sku ? '0' : '8');
  const [widthIn, setWidthIn] = useState(sku ? '0' : '6');
  const [heightIn, setHeightIn] = useState(sku ? '0' : '4');
  const [fieldsReady, setFieldsReady] = useState(!sku);
  const [webcamOn, setWebcamOn] = useState(false);
  const [scanMessage, setScanMessage] = useState<string | null>(null);
  const [shipping, setShipping] = useState(false);
  const [activeShipAction, setActiveShipAction] = useState<ShipConfirmAction | null>(null);
  const [shipSucceeded, setShipSucceeded] = useState(false);
  const [shipProgress, setShipProgress] = useState<string | null>(null);
  const [shipResults, setShipResults] = useState<ShipBatchOrderResult[] | null>(null);
  const [shipError, setShipError] = useState<string | null>(null);
  const [reprinting, setReprinting] = useState(false);
  const [canReprint, setCanReprint] = useState(false);
  const scanInFlightRef = useRef(false);

  useEffect(() => {
    if (!sku) {
      return;
    }
    let cancelled = false;
    setFieldsReady(false);
    void (async (): Promise<void> => {
      const pkg = await fetchSingleItemPackageWeights(sku);
      if (cancelled) {
        return;
      }
      const split = splitOunces(pkg.weightOz);
      setLbs(String(split.lbs));
      setOz(String(split.oz));
      setLengthIn(dimString(pkg.length));
      setWidthIn(dimString(pkg.width));
      setHeightIn(dimString(pkg.height));
      setFieldsReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [sku]);

  useEffect(() => {
    if (!showReprintButton) {
      return;
    }
    const orderIds = orders.map((order) => order.orderId).filter(Boolean);
    if (hasRememberedShipLabelPdf(orderIds)) {
      setCanReprint(true);
      return;
    }
    const orderId = orderIds[0];
    if (!orderId) {
      return;
    }
    let cancelled = false;
    void (async (): Promise<void> => {
      try {
        const status = await fetchShipLabelStatus(orderId);
        if (!cancelled && status.hasPurchasedLabel) {
          setCanReprint(true);
        }
      } catch {
        if (!cancelled) {
          setCanReprint(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orders, showReprintButton]);

  const handleClose = (): void => {
    if (shipping || reprinting) return;
    onClose();
  };

  const handleToggleBoxScan = (): void => {
    if (shipping || reprinting) return;
    if (webcamOn) {
      setWebcamOn(false);
      setScanMessage(null);
      return;
    }
    setWebcamOn(true);
  };

  const handleBoxSizeCapture = async (imageSrc: string | null): Promise<boolean> => {
    if (!imageSrc || shipping || reprinting || scanInFlightRef.current) {
      return false;
    }
    scanInFlightRef.current = true;
    setScanMessage('Processing image...');
    try {
      const scan = await readBoxDimensionsFromImage(imageSrc);
      if (scan.status === BOX_SCAN_STATUSES.NO_TEXT) {
        setScanMessage(BOX_SCAN_NO_TEXT_MESSAGE);
        return false;
      }
      if (scan.status === BOX_SCAN_STATUSES.NO_DIMS) {
        setScanMessage(boxScanNoDimsMessage(scan.ocrText));
        return false;
      }
      const { dims } = scan;
      setLengthIn(String(dims.length));
      setWidthIn(String(dims.width));
      setHeightIn(String(dims.height));
      setScanMessage(`Matched box size ${dims.length} × ${dims.width} × ${dims.height}.`);
      closeCameraAfterScanFlash(setWebcamOn);
      return true;
    } finally {
      scanInFlightRef.current = false;
    }
  };

  const canShip = orders.some((order) => Boolean(order.orderId));
  const orderIds = orders.map((order) => order.orderId).filter(Boolean);

  const handleReprint = async (): Promise<void> => {
    if (!showReprintButton || reprinting || shipping || !canReprint) {
      return;
    }
    const remembered = getRememberedShipLabelPdfs(orderIds);
    const fallbackOrderId = orderIds[0];
    if (remembered.length === 0 && !fallbackOrderId) {
      setShipError('No order to reprint');
      return;
    }
    setReprinting(true);
    setShipError(null);
    try {
      const result = remembered.length > 0
        ? await reprintShipLabels({ pdfs: remembered })
        : await reprintShipLabels({ orderId: fallbackOrderId });
      setShipProgress(
        `Reprinted ${result.printed} of ${result.total}`,
      );
      if (!result.success) {
        setShipError(result.error ?? 'Reprint failed');
      }
    } catch (err: unknown) {
      setShipError(getApiErrorMessage(err, 'Failed to reprint label'));
    } finally {
      setReprinting(false);
    }
  };

  const shipDisabled = shipping || reprinting || shipSucceeded || !canShip || !fieldsReady;
  const reprintDisabled = !canReprint || shipping || reprinting;

  const handleConfirmShip = async (action: ShipConfirmAction): Promise<void> => {
    if (shipping || reprinting || shipSucceeded) {
      return;
    }

    const totalOunces = (Number(lbs) || 0) * 16 + (Number(oz) || 0);
    const length = Number(lengthIn);
    const width = Number(widthIn);
    const height = Number(heightIn);

    if (!canShip) {
      setShipError('No order to ship');
      return;
    }
    if (!(totalOunces > 0) || !(length > 0) || !(width > 0) || !(height > 0)) {
      setShipError('Enter positive weight and L × W × H in inches');
      return;
    }

    setActiveShipAction(action);
    setShipping(true);
    setShipError(null);
    setShipResults(null);
    setWebcamOn(false);
    setShipProgress(`Shipping 0 of ${orderIds.length}…`);

    try {
      const result = await shipBatchOrders({
        orderIds,
        ...(sku ? { sku } : {}),
        ...(action === 'usps' ? USPS_GROUND_ADVANTAGE : {}),
        ...(showReprintButton ? { includeLabelData: true } : {}),
        weight: { value: totalOunces, units: 'ounces' },
        dimensions: { length, width, height, units: 'inches' },
      });
      setShipResults(result.results);
      setShipProgress(`Shipped ${result.shipped} of ${result.total}`);
      for (const row of result.results) {
        if (row.success && row.labelData) {
          rememberShipLabelPdf(row.orderId, row.labelData);
        }
      }
      if (result.results.some((row) => row.success)) {
        setCanReprint(true);
      }
      if (result.success) {
        setShipSucceeded(true);
        onFullyShipped?.();
      } else {
        setShipError('Some orders failed to ship or print');
      }
    } catch (err: unknown) {
      setShipError(getApiErrorMessage(err, 'Failed to ship batch'));
      setShipProgress(null);
    } finally {
      setShipping(false);
      setActiveShipAction(null);
    }
  };

  return (
    <div
      className="pbl-modal-overlay"
      onClick={handleClose}
      role="presentation"
    >
      <div
        className="pbl-modal single-sku-ship-modal"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="single-sku-ship-modal-title"
      >
        <button
          type="button"
          className="pbl-modal-close"
          onClick={handleClose}
          aria-label="Close"
          disabled={shipping || reprinting}
        >
          ×
        </button>
        <h2 id="single-sku-ship-modal-title" className="pbl-modal-title">
          Ship Orders
        </h2>
        <p className="single-sku-ship-subtitle">{subtitle}</p>

        <div className="single-sku-ship-form">
          <div className="single-sku-ship-field">
            <span>Weight</span>
            <LbsOzWeightInputs
              lbs={lbs}
              oz={oz}
              onLbsChange={setLbs}
              onOzChange={setOz}
              onLbsFocus={() => clearZeroOnFocus(lbs, setLbs)}
              onOzFocus={() => clearZeroOnFocus(oz, setOz)}
              disabled={shipping || reprinting || !fieldsReady}
            />
          </div>

          <fieldset className="single-sku-ship-dims" disabled={shipping || reprinting || !fieldsReady}>
            <legend>Box size (inches)</legend>
            <div className="single-sku-ship-dims-row">
              <label>
                <span>L</span>
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={lengthIn}
                  onChange={(e) => setLengthIn(e.target.value)}
                  onFocus={() => clearZeroOnFocus(lengthIn, setLengthIn)}
                />
              </label>
              <label>
                <span>W</span>
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={widthIn}
                  onChange={(e) => setWidthIn(e.target.value)}
                  onFocus={() => clearZeroOnFocus(widthIn, setWidthIn)}
                />
              </label>
              <label>
                <span>H</span>
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={heightIn}
                  onChange={(e) => setHeightIn(e.target.value)}
                  onFocus={() => clearZeroOnFocus(heightIn, setHeightIn)}
                />
              </label>
            </div>
            <button
              type="button"
              className="single-sku-action-button single-sku-ship-scan"
              onClick={handleToggleBoxScan}
            >
              {webcamOn ? 'Cancel scan' : 'Scan box size'}
            </button>
          </fieldset>

          {webcamOn ? (
            <ScannerSection
              mode="barcode"
              onCapture={handleBoxSizeCapture}
              webcamOn={webcamOn}
              setWebcamOn={setWebcamOn}
              className="webcam-capture-container"
            />
          ) : null}
          {scanMessage ? (
            <p className="single-sku-ship-scan-message">{scanMessage}</p>
          ) : null}
        </div>

        {shipProgress ? <p className="single-sku-action-message">{shipProgress}</p> : null}
        {shipError ? <p className="single-sku-error">{shipError}</p> : null}

        {shipResults ? (
          <ul className="single-sku-ship-results">
            {shipResults.map((row) => (
              <li key={row.orderId} className={row.success ? 'ok' : 'fail'}>
                {row.orderNumber}
                {row.success
                  ? ` · ${row.trackingNumber ?? 'shipped'}${row.existingLabel ? ' (existing label)' : ''}${row.printed === false ? ' (print failed)' : ''}`
                  : ` · ${row.error ?? 'failed'}`}
              </li>
            ))}
          </ul>
        ) : null}

        <div className="single-sku-ship-actions">
          {showUspsShipButton ? (
            <button
              type="button"
              className="single-sku-action-button single-sku-action-button-ship single-sku-ship-confirm"
              onClick={() => { void handleConfirmShip('usps'); }}
              disabled={shipDisabled}
              aria-busy={shipping && activeShipAction === 'usps'}
              title={!canShip ? 'No order to ship' : 'Buy and print USPS Ground Advantage'}
            >
              {shipping && activeShipAction === 'usps' ? (
                <span className="single-sku-ship-confirm-spinner" aria-hidden="true" />
              ) : null}
              {shipping && activeShipAction === 'usps'
                ? 'Shipping…'
                : 'Ship USPS'}
            </button>
          ) : null}
          <button
            type="button"
            className="single-sku-action-button single-sku-action-button-ship single-sku-ship-confirm"
            onClick={() => { void handleConfirmShip('default'); }}
            disabled={shipDisabled}
            aria-busy={shipping && activeShipAction === 'default'}
            title={!canShip ? 'No order to ship' : undefined}
          >
            {shipping && activeShipAction === 'default' ? (
              <span className="single-sku-ship-confirm-spinner" aria-hidden="true" />
            ) : null}
            {shipping && activeShipAction === 'default'
              ? 'Shipping…'
              : shipSucceeded
                ? 'Shipped'
                : 'Ship Orders'}
          </button>
          {showReprintButton ? (
            <button
              type="button"
              className="single-sku-action-button single-sku-action-button-reprint single-sku-ship-confirm"
              onClick={() => { void handleReprint(); }}
              disabled={reprintDisabled}
              aria-busy={reprinting}
              title={canReprint ? 'Reprint shipping label' : 'Reprint after a successful label purchase'}
            >
              {reprinting ? (
                <span className="single-sku-ship-confirm-spinner" aria-hidden="true" />
              ) : null}
              {reprinting ? 'Reprinting…' : 'Reprint'}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default ShipOrdersModal;
